import React, { useEffect, useRef, useState } from "react";
import Sidebar from "../../../Navigation/Sidebar/Sidebar";
import LoadingSpinner from "../../../Loading/component";
import {
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Popover,
  CircularProgress,
  Typography
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import downloadIcon from "../../../../assests/filedownload.svg"
import { format as formatDate } from 'date-fns';
import "./PartnerViewSettlement.css";

const PayoutViewSettlementComponent = ({
  loading,
  error,
  settledPayouts,
  totalCount,
  filters,
  defaultDates,
  partners,
  tempFilters,
  onFilterChange,
  onTempFilterChange,
  onApplyFilters,
  onLoadMore,
  hasMore,
  handleDownloadXLS,
  handleClearFilters,
  onFilterPopupOpen,
  onGoBack,
  onPartnerLoadMore,
  hasMorePartners,
  loadMorePartnersLoading
}) => {
  const tableContainerRef = useRef(null);
  const [dateRangeAnchorEl, setDateRangeAnchorEl] = useState(null);

  const handleDateRangeClick = (event) => {
    setDateRangeAnchorEl(event.currentTarget);
    onFilterPopupOpen(); // Call to reset temp filters
  };

  const handleDateRangeClose = () => {
    setDateRangeAnchorEl(null);
  };

  // Custom DatePicker component
  const CustomDatePicker = ({ label, value, onChange, placeholder }) => (
    <DatePicker
      value={value}
      onChange={onChange}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          size="small"
          placeholder={placeholder}
          onClick={(e) => e.stopPropagation()}
        />
      )}
      PopperProps={{
        placement: "bottom-start",
        style: {
          zIndex: 9999,
          position: 'fixed'
        }
      }}
      componentsProps={{
        actionBar: {
          actions: ['clear', 'today', 'accept'],
        },
      }}
      onOpen={() => { }}
      onClose={() => { }}
    />
  );

  // REMOVED the auto-scroll load more effect
  // Only keep for partner dropdown scroll
  const handlePartnerMenuScroll = (event) => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 &&
      hasMorePartners &&
      !loadMorePartnersLoading
    ) {
      onPartnerLoadMore();
    }
  };

  // Show current date range in button
  const getDateRangeText = () => {
    // If no custom filters are applied, show default range
    if (!filters.startDate || !filters.endDate || 
        (filters.startDate === defaultDates.startDate && 
         filters.endDate === defaultDates.endDate)) {
      return "Select Date Range";
    }
    return `${filters.startDate} to ${filters.endDate}`;
  };

  // Check if clear all button should be disabled
  const isClearAllDisabled = !filters.startDate && !filters.endDate && !filters.partnerId;

  const dateRangeOpen = Boolean(dateRangeAnchorEl);
  const dateRangeId = dateRangeOpen ? 'date-range-popover' : undefined;

  return (
    <div className="partner-view-settlement-container">
      {loading && settledPayouts.length === 0 ? (
        <LoadingSpinner />
      ) : (
        <>
          <Sidebar />
          <div className="partner-view-settlement-content-wrapper">

            <div className="partner-view-settlement-header">
              <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                <button
                  className="partner-view-settlement-back-button"
                  onClick={onGoBack}
                  aria-label="Go back"
                >
                  ←
                </button>
                <h1 className="partner-view-settlement-title">Settled Payouts </h1>

                <span className="partner-view-settlement-badge">Settled</span>
              </div>

              <Button
                variant="contained"
                onClick={handleDownloadXLS}
                disabled={settledPayouts.length === 0}
                className="partner-view-settlement-download-button"
              >
                <img src={downloadIcon} alt="download" />
              </Button>
            </div>

            {/* Filter Section */}
            <div className="partner-view-settlement-filter-section">
              <div className="partner-view-settlement-filter-header">
                <Button
                  variant="outlined"
                  color="success"
                  onClick={handleDateRangeClick}
                  aria-describedby={dateRangeId}
                  className="partner-view-settlement-date-range-button"
                >
                  {getDateRangeText()}
                </Button>
                <Button
                  variant="outlined"
                  color="success"
                  onClick={handleClearFilters}
                  disabled={isClearAllDisabled}
                  className={`partner-view-settlement-clear-all-button ${isClearAllDisabled ? 'disabled' : ''}`}
                >
                  Clear All
                </Button>

                <Popover
                  id={dateRangeId}
                  open={dateRangeOpen}
                  anchorEl={dateRangeAnchorEl}
                  onClose={handleDateRangeClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  className="partner-view-settlement-popover"
                >
                  <div className="partner-view-settlement-date-range-popover">
                    <h6 className="partner-view-settlement-popover-title">Apply filter</h6>
                    <div className="partner-view-settlement-date-range-filters">
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <div className="partner-view-settlement-date-filter">
                          <label>Start Date</label>
                          <CustomDatePicker
                            value={tempFilters.startDate}
                            onChange={(date) => onTempFilterChange('startDate', date)}
                            placeholder="Select start date"
                          />
                        </div>

                        <div className="partner-view-settlement-date-filter">
                          <label>End Date</label>
                          <CustomDatePicker
                            value={tempFilters.endDate}
                            onChange={(date) => onTempFilterChange('endDate', date)}
                            placeholder="Select end date"
                          />
                        </div>
                      </LocalizationProvider>
                    </div>
                    <div className="partner-view-settlement-filter-group">
                      <h4 className="partner-view-settlement-filter-subtitle">Select Partner</h4>
                      <FormControl fullWidth size="small">
                        <InputLabel className="partner-view-settlement-partner-label">Partner Name</InputLabel>
                        <Select
                          value={tempFilters.partnerId || ''}
                          onChange={(e) => onTempFilterChange('partnerId', e.target.value)}
                          label="Partner Name"
                          className="partner-view-settlement-partner-select"
                          MenuProps={{
                            className: "partner-view-settlement-partner-menu",
                            PaperProps: {
                              className: "partner-view-settlement-partner-menu-paper",
                              style: {
                                maxHeight: 250,
                              },
                              onScroll: handlePartnerMenuScroll, // Keep for partner dropdown
                            },
                            disableScrollLock: true
                          }}
                        >
                          <MenuItem value="">All Partners</MenuItem>
                          {partners.map(partner => (
                            <MenuItem key={partner.id} value={partner.id} className="partner-view-settlement-partner-item">
                              {partner.name}
                            </MenuItem>
                          ))}
                          
                          {/* Load More for Partners - auto-load on scroll */}
                          {hasMorePartners && (
                            <MenuItem 
                              disabled={loadMorePartnersLoading}
                              className="partner-view-settlement-partner-load-more-item"
                            >
                              {loadMorePartnersLoading ? (
                                <div className="partner-view-settlement-partner-loading">
                                  <CircularProgress size={16} style={{ marginRight: "8px" }} />
                                  Loading more...
                                </div>
                              ) : (
                                <div className="partner-view-settlement-partner-load-more-text">
                                  Scroll for more partners...
                                </div>
                              )}
                            </MenuItem>
                          )}
                          
                          {!hasMorePartners && partners.length > 0 && (
                            <MenuItem disabled className="partner-view-settlement-no-more-partners">
                              No more partners
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </div>
                    <div className="partner-view-settlement-filter-actions">
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          onApplyFilters();
                          handleDateRangeClose();
                        }}
                        className="partner-view-settlement-apply-button"
                      >
                        Apply
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleDateRangeClose}
                        className="partner-view-settlement-cancel-button"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </Popover>
              </div>
            </div>

            <div className="partner-view-settlement-table-section">
              <TableContainer
                component={Paper}
                ref={tableContainerRef}
                className="partner-view-settlement-table-container"
              >
                <Table stickyHeader className="partner-view-settlement-table">
                  <TableHead>
                    <TableRow>
                      <TableCell className="partner-view-settlement-table-header">Start Date</TableCell>
                      <TableCell className="partner-view-settlement-table-header">End Date</TableCell>
                      <TableCell className="partner-view-settlement-table-header">Cohort ID</TableCell>
                      <TableCell className="partner-view-settlement-table-header">Payout ID</TableCell>
                      <TableCell className="partner-view-settlement-table-header">Partner ID</TableCell>
                      <TableCell className="partner-view-settlement-table-header">Partner Name</TableCell>
                      <TableCell className="partner-view-settlement-table-header">Type</TableCell>
                      <TableCell className="partner-view-settlement-table-header">Revenue Today</TableCell>
                      <TableCell className="partner-view-settlement-table-header">Total Deduction</TableCell>
                      <TableCell className="partner-view-settlement-table-header">Net Revenue </TableCell>
                      <TableCell className="partner-view-settlement-table-header">Filtered Net Revenue </TableCell>
                      <TableCell className="partner-view-settlement-table-header">Payout</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {settledPayouts.length > 0 ? (
                      <>
                        {settledPayouts.map((payout) => (
                          <TableRow key={`${payout.payoutId}-${payout.billId}`} className="partner-view-settlement-table-row">
                            <TableCell>
                              {payout.cohortSalesStart
                                ? new Date(payout.cohortSalesStart).toLocaleString("en-IN", {
                                  timeZone: "Asia/Kolkata",
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  second: "2-digit",
                                })
                                : "N/A"}
                            </TableCell>

                            <TableCell>
                              {payout.cohortSalesEnd
                                ? new Date(payout.cohortSalesEnd).toLocaleString("en-IN", {
                                  timeZone: "Asia/Kolkata",
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  second: "2-digit",
                                })
                                : "N/A"}
                            </TableCell>

                            <TableCell>{payout.cohortId || 'N/A'}</TableCell>
                            <TableCell>{payout.payoutId || 'N/A'}</TableCell>
                            <TableCell>{payout.franchiseeId || 'N/A'}</TableCell>
                            <TableCell>{payout.name || 'N/A'}</TableCell>
                            <TableCell>{payout.type || 'N/A'}</TableCell>
                            <TableCell>₹ {payout.revenueToday?.toLocaleString("en-IN")}</TableCell>
                            <TableCell>₹ {payout.totalDeductions?.toLocaleString("en-IN")}</TableCell>
                            <TableCell>₹ {payout.netPayable?.toLocaleString("en-IN")}</TableCell>
                            <TableCell>₹ {payout.cumulativeAmount?.toLocaleString("en-IN")}</TableCell>
                            <TableCell>₹ {payout.generatedPayout?.toLocaleString("en-IN")}</TableCell>
                          </TableRow>
                        ))}

                        {/* Load More for Table - Only button, no auto-scroll */}
                        {hasMore && (
                          <TableRow className="partner-view-settlement-load-more-row">
                            <TableCell colSpan={12} align="center">
                              <Button
                                onClick={onLoadMore}
                                disabled={loading}
                                className="partner-view-settlement-table-load-more-button"
                                variant="outlined"
                              >
                                {loading ? (
                                  <>
                                    <CircularProgress size={16} style={{ marginRight: "8px" }} />
                                    Loading...
                                  </>
                                ) : (
                                  "Load More"
                                )}
                              </Button>
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    ) : (
                      <TableRow>
                        <TableCell colSpan={12} align="center" className="partner-view-settlement-no-data">
                          {loading ? 'Loading...' : 
                            filters.startDate && filters.endDate ? 
                            `No settled payouts found for ${filters.startDate} to ${filters.endDate}` :
                            'No settled payouts found for default date range'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PayoutViewSettlementComponent;